<script setup lang = "ts">
import {useCarDataStore} from "~/store/carData";
import type {ICarFilter} from "~/interfaces/ICarFilter";

defineProps({
	showClearFilters: Boolean
})
const carFilters = defineModel<ICarFilter>({required: true})
const emits = defineEmits(['filter', 'clearFilter', 'closeDialog'])

const {
	colors,
	drive_trains,
	transmissions,
	fuel_types,
	interior_types,
	body_styles,
	prices_min,
	prices_max,
	mileageFilters,
	years
} = useCarFilters()
const carDataStore = useCarDataStore()
const models = ref<string[]>([])

const priceMin = computed<any>({
	get: () => {
		return prices_min.find((price: any) => price.value === Number.parseInt(`${carFilters.value.price.value.min}`)) ?? ''
	},
	set: (val: string) => carFilters.value.price.value.min = val
})
const priceMax = computed<any>({
	get: () => {
		return prices_max.find((price: any) => price.value === Number.parseInt(`${carFilters.value.price.value.max}`)) ?? ''
	},
	set: (val: string) => carFilters.value.price.value.max = val
})
const mileageMin = computed<any>({
	get: () => mileageFilters.find((mileage: any) => mileage.value === Number.parseInt(`${carFilters.value.millage.value.min}`)) ?? '',
	set: (val: string) => carFilters.value.millage.value.min = val
})
const mileageMax = computed<any>({
	get: () => mileageFilters.find((mileage: any) => mileage.value === Number.parseInt(`${carFilters.value.millage.value.max}`)) ?? '',
	set: (val: string) => carFilters.value.millage.value.max = val
})

const getModels = (make: string, clearModel = true) => {
	if (clearModel) carFilters.value.model.value = '' // Clear Model
	models.value = carDataStore.getModelByMake(make)
}
const clearFilters = () => {
	emits('clearFilter')
}

onMounted(() => {
	setTimeout(() => {
		if (carFilters.value.make.value)
			models.value = carDataStore.getModelByMake(`${carFilters.value.make.value}`)
	}, 300)
})

defineExpose({getModels})
</script>

<template>
	<div class = "flex flex-col pb-10">
		<q-list bordered>
			<div class = "px-5 relative">
				<h2 class = "text-xl mt-5 mb-5">Filtros</h2>
				<q-btn flat
							 rounded
							 color = "negative"
							 class = "min-[1024px]:hidden absolute top-[5px] right-[5px]"
							 size = "sm"
							 icon = "fa-solid fa-times"
							 dense
							 @click = "$emit('closeDialog')"/>
			</div>
			
			<div class = "grid grid-cols-1 gap-5 px-5 pb-10">
				<q-btn v-if = "showClearFilters" outline color = "warning"
							 padding = "5px"
							 size = "md"
							 @click = "clearFilters">
					<i class = "fa-solid fa-trash mr-2"/>Limpiar Filtros
				</q-btn>
				<div class = "flex w-full">
					<q-btn-toggle
							v-model = "carFilters.condition.value"
							dense
							spread
							unelevated
							class = "border border-solid border-1 border-neutral-300 w-full"
							toggle-color = "primary"
							:options = "[{label: 'Todos', value: ''},{label: 'Nuevos', value: 'NEW'},{label: 'Usados', value: 'USED'}]"
					/>
				</div>
				<q-select dense outlined v-model = "carFilters.make.value"
									:options = "carDataStore.getMakes"
									option-label = "make"
									options-cover
									map-options
									@update:model-value = "getModels"
									behavior = "default"
									clearable
									label = "Marca"/>
				<q-select dense outlined behavior = "default"
									v-model = "carFilters.model.value"
									clearable
									:options = "models"
									options-cover
									label = "Modelo"/>
				<div class = "grid grid-cols-2 gap-2">
					<p class = "text-sm col-span-2">Precio:</p>
					<q-select dense outlined v-model = "priceMin"
										:options = "prices_min"
										map-options
										emit-value
										options-cover
										label = "Desde"/>
					<q-select dense outlined v-model = "priceMax"
										:options = "prices_max"
										map-options
										emit-value
										options-cover
										option-value = "value"
										option-label = "label"
										label = "Hasta"/>
				</div>
				<div class = "grid grid-cols-2 gap-2">
					<p class = "text-sm col-span-2">Millage:</p>
					<q-select dense outlined v-model = "mileageMin"
										:options = "mileageFilters"
										map-options
										options-cover
										emit-value option-label = "label"
										option-value = "value"
										label = "Desde"/>
					<q-select dense outlined v-model = "mileageMax"
										:options = "mileageFilters"
										map-options
										options-cover
										emit-value
										option-label = "label"
										option-value = "value"
										label = "Hasta"/>
				</div>
				<div class = "grid grid-cols-2 gap-2">
					<p class = "text-sm col-span-2">Año:</p>
					<q-select dense outlined v-model = "carFilters.year.value.min"
										:options = "years"
										map-options
										options-cover
										emit-value
										option-label = "label"
										option-value = "value"
										label = "Desde"/>
					<q-select dense outlined v-model = "carFilters.year.value.max"
										:options = "years"
										map-options
										options-cover
										emit-value
										option-label = "label"
										option-value = "value"
										label = "Hasta"/>
				</div>
			</div>
			<q-separator/>
			<q-expansion-item label = "Detalles">
				<div class = "grid grid-cols-1 gap-5 px-5 pt-5 pb-10">
					<q-select dense v-model = "carFilters.fuel_type.value"
										use-chips
										outlined
										multiple
										label = "Combustible"
										:options = "fuel_types"/>
					<q-select dense v-model = "carFilters.transmission.value"
										use-chips
										outlined
										multiple
										label = "Transmisión"
										:options = "transmissions"/>
					<q-select dense v-model = "carFilters.drive_train.value"
										use-chips
										outlined
										multiple
										label = "Tren de manejo"
										:options = "drive_trains"/>
					<q-select dense v-model = "carFilters.body_style.value"
										use-chips
										outlined
										multiple
										label = "Tipo"
										:options = "body_styles"
										option-label = "body_style"
										option-value = "body_style"
					/>
				</div>
			</q-expansion-item>
			<q-separator/>
			<q-expansion-item label = "Colores e Interior">
				<div class = "grid grid-cols-1 gap-5 px-5 pt-5 pb-10">
					<q-select dense v-model = "carFilters.color.value"
										use-chips
										:options = "colors.exterior"
										multiple
										map-options
										emit-value
										option-label = "name"
										option-value = "name"
										outlined
										label = "Color Exterior">
						<template v-slot:option = "scope">
							<q-item v-bind = "scope.itemProps">
								<q-item-section avatar>
									<div class = "w-[25px] h-[25px] rounded-full border-solid border-gray-200 border"
											 :style = "`background-color: rgb(${scope.opt.rgb});`"></div>
								</q-item-section>
								<q-item-section>
									<q-item-label>{{ scope.opt.name }}</q-item-label>
								</q-item-section>
							</q-item>
						</template>
					</q-select>
					<q-select dense v-model = "carFilters.interior_color.value"
										use-chips
										:options = "colors.interior"
										multiple
										map-options
										emit-value
										option-label = "name"
										option-value = "name"
										outlined
										label = "Color Interior">
						<template v-slot:option = "scope">
							<q-item v-bind = "scope.itemProps">
								<q-item-section avatar>
									<div class = "w-[25px] h-[25px] rounded-full border-solid border-gray-200 border"
											 :style = "`background-color: rgb(${scope.opt.rgb});`"></div>
								</q-item-section>
								<q-item-section>
									<q-item-label>{{ scope.opt.name }}</q-item-label>
								</q-item-section>
							</q-item>
						</template>
					</q-select>
					<q-select dense v-model = "carFilters.interior_type.value"
										use-chips
										:options = "interior_types"
										multiple
										map-options
										emit-value
										outlined
										label = "Tipo de Interior"/>
				</div>
			</q-expansion-item>
		</q-list>
	</div>
</template>

<style scoped>

</style>

<style>
div[role="listbox"].q-menu, body.mobile:not(.native-mobile) .q-select__dialog {
	background: rgba(255, 255, 255, 0.63);
	border-radius: 16px;
	box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
	backdrop-filter: blur(13.2px);
	-webkit-backdrop-filter: blur(13.2px);
}

body.mobile:not(.native-mobile) .q-select__dialog {
	max-height: calc(100vh - 180px) !important;
}

@media screen and (max-width: 500px) {
	div[role="listbox"].q-menu {
		max-height: calc(100vh - 80px) !important;
		
	}
}
</style>